import moment from "moment";
import {
  DAY_TIME_INTERVALS,
  DAY_TIME_INTERVALS_HOURS,
  DURATION_FORMAT,
  EARLIEST_TIME,
  HEIGHT_OF_ONE_HOUR,
  ONE_HOUR,
  SNAP_INTERVAL_IN_MINUTES,
} from "@miplatform/constants";
import { snapAdjusted } from "../roundNumber/roundNumber";

const SUNDAY = 0;
const SATURDAY = 6;
/**
 * Returns a moment object for the provided date or current date if none provided
 * @param {moment.MomentInput} date - Date to convert to moment object
 * @returns {moment.Moment} Moment object
 */
export const getDate = (date = null) => (date ? moment(date) : moment());

/**
 * Returns a moment object set to the start of day for the provided date or current date
 * @param {moment.MomentInput} date - Date to convert to moment object
 * @returns {moment.Moment} Moment object set to start of day
 */
export const getDateDay = (date = null) =>
  date ? moment(date).startOf("day") : moment().startOf("day");

/**
 * Returns a moment object for the previous Monday (start of ISO week)
 * @returns {moment.Moment} Moment object for last Monday
 */
export const getLastMonday = () => moment().startOf("isoWeek");

/**
 * Subtracts a specified amount of time from a date
 * @param {moment.MomentInput} date - Base date
 * @param {number} amount - Amount to subtract
 * @param {string} [unit] - Unit of time (e.g., 'days', 'hours')
 * @returns {moment.Moment} New moment object with subtracted time
 */
export const subtractFromDate = (date, amount, unit = "days") =>
  moment(date).subtract(amount, unit);

/**
 * Adds a specified amount of time to a date
 * @param {moment.MomentInput} date - Base date
 * @param {number} amount - Amount to add
 * @param {string} [unit] - Unit of time (e.g., 'days', 'hours')
 * @returns {moment.Moment} New moment object with added time
 */
export const addToDate = (date, amount, unit = "days") =>
  moment(date).add(amount, unit);

/**
 * Adds a specified amount of business time (excluding weekends) to a date
 * @param {moment.MomentInput} date - Base date
 * @param {number} amount - Amount to add
 * @param {string} unit - Unit of time ('days', 'hours')
 * @returns {moment.Moment} New moment object with added business time
 */
export const addToBusinessDate = (date, amount, unit) => {
  const result = moment(date);
  const unitLoweredCase = unit.toLowerCase();

  if (["days", "day", "d"].includes(unitLoweredCase)) {
    for (let i = 0; i < amount; ) {
      result.add(1, "day");
      if (result.day() > SUNDAY && result.day() < SATURDAY) {
        i++;
      }
    }

    return result;
  }

  if (["hours", "hour", "h"].includes(unitLoweredCase)) {
    const hoursPerDay = 24;
    const fullDays = Math.floor(amount / hoursPerDay);
    const addedDaysResult = addToBusinessDate(date, fullDays, "days");

    for (let i = 0; i < amount % hoursPerDay; ) {
      addedDaysResult.add(1, "hour");
      if (addedDaysResult.day() > SUNDAY && addedDaysResult.day() < SATURDAY) {
        i++;
      }
    }

    return addedDaysResult;
  }

  return addToDate(date, amount, unit);
};

/**
 * Subtracts a specified amount of business time (excluding weekends) from a date
 * @param {moment.MomentInput} date - Base date
 * @param {number} amount - Amount to subtract
 * @param {string} unit - Unit of time ('days', 'hours')
 * @returns {moment.Moment} New moment object with subtracted business time
 */
export const subtractFromBusinessDate = (date, amount, unit = "days") => {
  const result = moment(date);
  const unitLoweredCase = unit.toLowerCase();

  if (["days", "day", "d"].includes(unitLoweredCase)) {
    for (let i = 0; i < amount; ) {
      result.subtract(1, "day"); // Moving backwards
      // Check if the new day is a weekday
      if (result.day() > SUNDAY && result.day() < SATURDAY) {
        i++;
      }
    }
    return result;
  }

  if (["hours", "hour", "h"].includes(unitLoweredCase)) {
    const hoursPerDay = 24;
    const fullDays = Math.floor(amount / hoursPerDay);

    const subtractedDaysResult = subtractFromBusinessDate(
      date,
      fullDays,
      "days"
    );

    for (let i = 0; i < amount % hoursPerDay; ) {
      subtractedDaysResult.subtract(1, "hour");
      if (
        subtractedDaysResult.day() > SUNDAY &&
        subtractedDaysResult.day() < SATURDAY
      ) {
        i++;
      }
    }

    return subtractedDaysResult;
  }

  return subtractFromDate(date, amount, unit);
};

/**
 * Formats a date according to the specified format
 * @param {moment.MomentInput} date - Date to format
 * @param {string} format - Format string (Moment.js format)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format) => moment(date).format(format);

/**
 * Converts a date to ISO string format
 * @param {moment.MomentInput} [date] - Date to convert
 * @returns {string} ISO string representation of the date
 */
export const getDateISO = (date) => moment(date).toISOString();

/**
 * Checks if two dates fall on the same day
 * @param {moment.MomentInput} date1 - First date
 * @param {moment.MomentInput} date2 - Second date
 * @returns {boolean} True if dates are on the same day
 */
export const isSameDay = (date1, date2) =>
  moment(date1).isSame(moment(date2), "day");

/**
 * Checks if two times are the same, down to the minute
 * @param {moment.MomentInput} time1 - First time
 * @param {moment.MomentInput} time2 - Second time
 * @returns {boolean} True if times are the same
 */
export const isSameTime = (time1, time2) =>
  moment(time1).isSame(moment(time2), "minute");

/**
 * Checks if a date is today
 * @param {moment.MomentInput} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => isSameDay(date, moment());

/**
 * Checks if a date is before the current time (to the minute)
 * @param {moment.MomentInput} date - Date to check
 * @returns {boolean} True if date is before now
 */
export const isBeforeNow = (date) => moment(date).isBefore(moment(), "minute");

/**
 * Checks if date1 is before date2 (by day)
 * @param {moment.MomentInput} [date1] - First date
 * @param {moment.MomentInput} [date2] - Second date
 * @returns {boolean} True if date1 is before date2
 */
export const isBefore = (date1, date2) =>
  moment(date1).isBefore(moment(date2), "day");

/**
 * Checks if date1 is after date2 (by day)
 * @param {moment.MomentInput} [date1] - First date
 * @param {moment.MomentInput} [date2] - Second date
 * @returns {boolean} True if date1 is after date2
 */
export const isDateAfter = (date1, date2) =>
  moment(date1).isAfter(moment(date2), "day");

/**
 * Checks if date1 is the same as or after date2 with specified precision
 * @param {moment.MomentInput} date1 - First date
 * @param {moment.MomentInput} date2 - Second date
 * @param {string} precision - Time unit for comparison (default: "day")
 * @returns {boolean} True if date1 is same as or after date2
 */
export const isSameOrAfter = (date1, date2, precision = "day") =>
  moment(date1).isSameOrAfter(moment(date2), precision);

/**
 * Checks if date1 is the same as or before date2 with specified precision
 * @param {moment.MomentInput} date1 - First date
 * @param {moment.MomentInput} date2 - Second date
 * @param {string} precision - Time unit for comparison (default: "day")
 * @returns {boolean} True if date1 is same as or before date2
 */
export const isSameOrBefore = (date1, date2, precision = "day") =>
  moment(date1).isSameOrBefore(moment(date2), precision);

/**
 * Sets the time portion of a date based on a time string (HH:MM)
 * @param {moment.MomentInput} date1 - Base date
 * @param {string} time - Time string in format "HH:MM"
 * @returns {moment.Moment} Moment object with updated time
 */
export const setTime = (date1, time) => {
  const timeParts = time.split(":");
  const requestedStartDate = moment(date1).set({
    hour: timeParts[0],
    minute: timeParts[1],
    second: 0,
  });
  return requestedStartDate;
};

/**
 * Returns human-readable time elapsed since the provided time
 * @param {moment.MomentInput} time - Time to calculate elapsed time from
 * @returns {string} Human-readable string representing elapsed time
 */
export const getTimeFromNow = (time) => {
  const result = moment(time).fromNow(true);

  switch (result) {
    case "a minute":
      return "1 minute";
    case "an hour":
      return "1 hour";
    case "a day":
      return "1 day";
    case "a month":
      return "1 month";
    case "a year":
      return "1 year";
    default:
      return result;
  }
};

/**
 * Converts a date to a decimal hour value
 * @param {moment.MomentInput} date - Date to convert
 * @returns {number} Decimal hour value
 */
export const toHour = (date) => {
  const time = new Date(date);
  const hours = time.getHours();
  const minutes = time.getMinutes() + (time.getSeconds() === 59);

  return Math.max(hours + minutes / ONE_HOUR, EARLIEST_TIME);
};

/**
 * Converts a date to a time string based on the provided format
 * @param {moment.MomentInput} date - Date to convert
 * @param {string} format - Format string (default: "HH:mm")
 * @returns {string} Formatted time string
 */
export const formatTime = (date, format = "HH:mm") =>
  moment(date).format(format);

/**
 * Formats a date with an adjustment for seconds (adds 1 if seconds are 59)
 * @param {Moment|moment.Moment|null|undefined} date - Date to format
 * @param {string} format - Format string (Moment.js format)
 * @returns {string} Formatted date string with seconds adjustment
 */
export const toFormattedDate = (date, format = "") => {
  if (!date || !moment(date).isValid()) {
    return "--/--/----";
  }

  const result = moment(date);

  return result.add({ s: result.seconds() === 59 ? 1 : 0 }).format(format);
};

/**
 * Creates a new date with specified decimal hours value
 * @param {moment.MomentInput} date - Base date
 * @param {number} hours - Decimal hour value
 * @param {string} snapMethod - Method for snapping minutes ('round', 'floor', 'ceil')
 * @returns {moment.Moment} New moment object with updated hours
 */
export const getDateWithNewHours = (date, hours, snapMethod = "round") => {
  const hour = Math.floor(hours);
  const minute = snapAdjusted(
    (hours - hour) * ONE_HOUR,
    SNAP_INTERVAL_IN_MINUTES,
    snapMethod
  );

  return moment(date).set({ hour, minute, second: 0 });
};

/**
 * Snaps a time to the nearest interval based on snap method
 * @param {moment.MomentInput} time - Time to snap
 * @param {string} snapMethod - Method for snapping ('round', 'floor', 'ceil')
 * @returns {moment.Moment} Snapped moment object
 */
export const toSnappedTime = (time, snapMethod = "round") =>
  getDateWithNewHours(time, toHour(time), snapMethod);

/**
 * Formats a duration in hours according to the specified format
 * @param {number} hours - Duration in hours
 * @param {string} format - Format string (default: DURATION_FORMAT)
 * @returns {string} Formatted duration string
 */
export const getFormattedDuration = (hours, format = DURATION_FORMAT) => {
  const result = moment({
    hours: Math.floor(hours),
    minutes: Math.round((hours * ONE_HOUR) % ONE_HOUR),
  }).format(format);

  return result === "Invalid date"
    ? moment({ hours: 0 }).format(format)
    : result;
};

/**
 * Calculates the duration between two times in hours
 * @param {moment.MomentInput} startTime - Start time
 * @param {moment.MomentInput} endTime - End time
 * @returns {number} Duration in hours
 */
export const getDurationInHours = (startTime, endTime) =>
  toHour(endTime) - toHour(startTime);

/**
 * Adds time to a date and optionally formats the result
 * @param {moment.MomentInput} date - Base date
 * @param {*} time - Time to add
 * @param {string} format - Optional format string
 * @returns {string} New date with added time, formatted if specified
 */
export const getDateWithAddedTime = (date, time, format = "") =>
  moment(date).add(time).format(format);

/**
 * Calculates a position value based on hour and earliest time
 * @param {number} hour - Hour value
 * @param {number} earliestTime - Earliest time constant (default: EARLIEST_TIME)
 * @returns {number} Position value
 */
export const getPosition = (hour, earliestTime = EARLIEST_TIME) =>
  (hour - earliestTime) * HEIGHT_OF_ONE_HOUR;

/**
 * Gets the current time with position information
 * @returns {Object} Object with position, value, and hour properties
 */
export const getCurrentTime = () => {
  const now = moment();
  const hour = toHour(now.format());

  return {
    position: getPosition(hour),
    value: now,
    hour,
  };
};

/**
 * Checks if a date was created within the specified number of minutes
 * @param {moment.MomentInput} createdOn - Creation date
 * @param {number} minutes - Number of minutes to check within (default: 15)
 * @returns {boolean} True if date was recently created
 */
export const isRecentlyAdded = (createdOn, time = 15, format = "minutes") => {
  if (!createdOn) return false;

  const now = moment();
  const createdDate = moment(createdOn);
  return now.diff(createdDate, format) <= time;
};

/**
 * Returns the start of a time unit for a date
 * @param {string} timeUnit - Unit of time ('day', 'month', etc.)
 * @param {moment.MomentInput} date - Date to find start of time unit
 * @returns {moment.Moment} Moment object set to start of time unit
 */
export const startOf = (timeUnit, date) => {
  return moment(date).startOf(timeUnit);
};

/**
 * Returns the end of a time unit for a date
 * @param {string} timeUnit - Unit of time ('day', 'month', etc.)
 * @param {moment.MomentInput} date - Date to find end of time unit
 * @returns {moment.Moment} Moment object set to end of time unit
 */
export const endOf = (timeUnit, date) => {
  return moment(date).endOf(timeUnit);
};

/**
 * Gets the difference between two times in specified units
 * @param {Object} params - Parameters object
 * @param {moment.MomentInput} params.start - Start time
 * @param {moment.MomentInput} params.end - End time
 * @param {string} params.timeUnit - Unit for difference calculation
 * @param {boolean} params.precise - Whether to return floating point value
 * @returns {number} Time difference in specified unit
 */
export const getTimeDifference = ({ start, end, timeUnit, precise }) => {
  const momentStart = moment(start);
  const momentEnd = moment(end);

  return momentEnd.diff(momentStart, timeUnit, precise);
};

const getTimeText = (time, timeUnit) => {
  if (timeUnit === "days") {
    return time === 1 ? `${time} day` : `${time} days`;
  }
  if (timeUnit === "hours") {
    return time === 1 ? `${time} hour` : `${time} hours`;
  }
  if (timeUnit === "minutes") {
    return time === 1 ? `${time} minute` : `${time} minutes`;
  }
  return "";
};

/**
 * Gets the difference between two times in specified units
 * @param {Object} params - Parameters object
 * @param {moment.MomentInput} params.start - Start time
 * @param {moment.MomentInput} params.end - End time
 * @returns {string} Time difference in specified unit
 */
export const getTimeDifferenceDurationText = ({ start, end }) => {
  const timeDiff = getTimeDifference({ start, end });
  const duration = moment.duration(timeDiff);
  return duration.days()
    ? getTimeText(duration.days(), "days")
    : duration.hours()
    ? getTimeText(duration.hours(), "hours")
    : duration.minutes()
    ? getTimeText(duration.minutes(), "minutes")
    : "Less than 1 minute";
};

/**
 * Creates a duration object from a date and time unit
 * @param {moment.Moment|number|object} date - Time value
 * @param {string} [timeUnit] - Unit of time (optional)
 * @returns {moment.Duration} Moment duration object
 */
export const getDuration = (date, timeUnit) => {
  return moment.duration(date, timeUnit);
};

/**
 * Converts a time to UTC
 * @param {moment.MomentInput} time - Time to convert
 * @returns {moment.Moment} UTC moment object
 */
export const getUTC = (time) => moment.utc(time);

/**
 * Checks if two dates are the same with specified time unit precision
 * @param {Object} params - Parameters object
 * @param {moment.MomentInput} params.date1 - First date
 * @param {moment.MomentInput} params.date2 - Second date
 * @param {string} params.timeUnit - Time unit for comparison
 * @returns {boolean} True if dates are the same
 */
export const isSame = ({ date1, date2, timeUnit }) => {
  return moment(date1).isSame(moment(date2), timeUnit);
};

/**
 * Converts a date to ISO string format (alias for getDateISO)
 * @param {moment.MomentInput} [date] - Date to convert
 * @returns {string} ISO string representation of the date
 */
export const getISOString = (date) => moment(date).toISOString();

/**
 * Subtracts a specified amount from a date
 * @param {number} amountToSubstract - Amount to subtract
 * @param {string} timeUnit - Unit of time
 * @param {moment.MomentInput} date - Base date
 * @returns {moment.Moment} New moment object with time subtracted
 */
export const getSubstraction = (amountToSubstract, timeUnit, date) => {
  return moment(date).subtract(amountToSubstract, timeUnit);
};

/**
 * Formats a date according to the specified format (different parameter order than formatDate)
 * @param {string} format - Format string (Moment.js format)
 * @param {moment.MomentInput} [date] - Date to format
 * @returns {string} Formatted date string
 */
export const getFormattedDate = (format, date) => {
  return moment(date).format(format);
};

/**
 * Returns a date formatted as MM/DD/YY
 * @param {moment.MomentInput} date - Date to format
 * @returns {string} Date formatted as MM/DD/YY
 */
export const getDefaultDate = (date = null) => {
  if (!date) {
    return "--/--/--";
  }
  return moment(date).format("MM/DD/YY");
};

/**
 * Returns a human-readable relative date string
 * @param {moment.MomentInput} date - Date to humanize
 * @returns {string} Human-readable relative date string
 */
export const humanizeDate = (date) => {
  const momentDate = moment(date);
  const duration = moment.duration(-1 * moment().diff(momentDate));

  return duration.humanize(true);
};

/**
 * Returns a string with the month and day, with relative phrasing
 * @param {moment.MomentInput} date - Date to humanize
 * @returns {string} string with the month and day, with relative phrasing
 */
export const getDayTitle = (date) => {
  const momentDate = moment(date);

  const today = moment();
  const tomorrow = moment().add(1, "days");
  const yesterday = moment().subtract(1, "days");

  const monthDate = momentDate.format("MMMM Do");

  if (momentDate.isSame(today, "day")) {
    return `Today, ${monthDate}`;
  }
  if (momentDate.isSame(tomorrow, "day")) {
    return `Tomorrow, ${monthDate}`;
  }
  if (momentDate.isSame(yesterday, "day")) {
    return `Yesterday, ${monthDate}`;
  }

  return momentDate.format("dddd, MMMM Do");
};

/**
 * Gets the difference between two dates in days
 * @param {moment.MomentInput} date1 - First date
 * @param {moment.MomentInput} date2 - Second date
 * @returns {number} Difference in days
 */
export const getDayDifference = (date1, date2) => {
  return moment(date1).diff(moment(date2), "days");
};

/**
 * Formats a date if it's valid, returns null otherwise
 * @param {moment.Moment|null} date - Date to format
 * @param {string} format - Format string (Moment.js format)
 * @returns {string|null} Formatted date string or null if date is invalid
 */
export const formatDateIfValid = (date, format) => {
  if (!date) {
    return null;
  }

  const updatedDate =
    typeof date === "string" && date.includes("T") ? date.split("T")[0] : date;

  const momentObj = moment(updatedDate, true);

  if (momentObj.isValid()) {
    return formatDate(date, format);
  }

  return null;
};

/**
 * Checks if the given date falls on a weekend (Saturday or Sunday).
 * @param {moment.MomentInput} date - The date to check. Can be a Date object, date string, or moment object.
 * @returns {boolean} Returns true if the date is a weekend day (Saturday or Sunday), false otherwise.
 */
export const isWeekendDay = (date) => {
  const day = moment(date).day();
  return day === 0 || day === 6; // Sunday or Saturday
};

/**
 * Adds a specified number of working days (excludes weekends) to a date
 * @param {number} workDays - Number of working days to add (can be negative to subtract, default: 0)
 * @param {moment.MomentInput} start - Starting date (defaults to current date)
 * @returns {moment.Moment} New moment object with added working days
 */
export const addWorkDays = (workDays = 0, start = getDate()) => {
  if (workDays === 0) {
    return start;
  }

  const isNegative = workDays < 0;
  const absoluteWorkDays = Math.abs(workDays);
  const direction = isNegative ? -1 : 1;

  let daysProcessed = 0;
  let current = start.clone();

  while (daysProcessed < absoluteWorkDays) {
    current = current.add(direction, "days");

    if (!isWeekendDay(current)) {
      daysProcessed++;
    }
  }

  return current;
};

/**
 * Adds a specified number of working hours (excludes weekends) to a date
 * @param {number} workHours - Number of working hours to add (default: 0)
 * @param {moment.MomentInput} start - Starting date (defaults to current date)
 * @returns {moment.Moment} New moment object with added working hours
 */
export const addWorkHours = (workHours = 0, start = getDate()) => {
  if (workHours === 0) {
    return getDate();
  }

  let hoursAdded = 0;
  let current = start;

  while (hoursAdded < workHours) {
    current = current.add(1, "hours");

    if (current.day() !== 6 && current.day() !== 0) {
      hoursAdded++;
    }
  }

  return current;
};

export const getTimeOfDay = (time) => {
  const currentHour = (typeof time === "string" ? moment(time) : time).hour();

  if (currentHour >= 7 && currentHour < 12) {
    return DAY_TIME_INTERVALS.MORNING;
  }
  if (currentHour >= 12 && currentHour < 17) {
    return DAY_TIME_INTERVALS.AFTERNOON;
  }
  if (currentHour >= 17 && currentHour < 21) {
    return DAY_TIME_INTERVALS.EVENING;
  }

  return DAY_TIME_INTERVALS.NIGHT;
};

export const getTimeOfDayInterval = (timeOfDay) =>
  `(${DAY_TIME_INTERVALS_HOURS[timeOfDay].start} - ${DAY_TIME_INTERVALS_HOURS[timeOfDay].end})`;

export const hoursToMinutes = (hours) => {
  return hours ? moment.duration(hours, "hours").asMinutes() : null;
};

export const minutesToHours = (minutes) => {
  return minutes ? moment.duration(minutes, "minutes").asHours() : null;
};

export const addSeconds = (time) => {
  return time ? moment(time, "HH:mm").format("HH:mm:ss") : null;
};

export const areSameDayOrBothNull = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return isSameDay(a, b);
};

/**
 * Converts hours and minutes to total minutes
 * @param {number} hours - Number of hours
 * @param {number} minutes - Number of minutes
 * @returns {number} Total minutes
 */
export const toMinutes = (hours, minutes) =>
  getDuration({ hours, minutes }).asMinutes();

/**
 * Extracts hours and minutes from a duration in minutes
 * @param {number} duration - Duration in minutes
 * @returns {{hours: number, minutes: number}} Object with hours and minutes
 */
export const getHoursAndMinutes = (duration = 0) => {
  const momentDuration = getDuration(duration, "minutes");
  return {
    hours: momentDuration.hours(),
    minutes: momentDuration.minutes(),
  };
};

export const getDaysBetween = (from, to) => {
  const days = [];
  const startDate = getDate(from).startOf("day");
  const endDate = getDate(to).endOf("day");
  const d = startDate.clone();
  while (d.isSameOrBefore(endDate)) {
    days.push(d.clone());
    d.add(1, "day");
  }
  return days;
};
